import { ActionReducerMapBuilder } from "@reduxjs/toolkit"

import { BikeFleetStateProps, StatusEnum } from "../types/bikeFleetType"
import { getBikeFleetThunk, updateBikeStatusThunk } from "./BikeFleetThunk"
import { CheckOutStateEnum } from "store/type"
import { transformBikeFleet } from "../utils"
import { isNotEmpty } from "utils"

export const handleBikeFleetBuilder = (builder: ActionReducerMapBuilder<BikeFleetStateProps>) => {
	// Handles state changes when fetching the bike fleet data.
	builder.addCase(getBikeFleetThunk.pending, (state) => {
		// When the fetch starts, set the bike fleet checkout state to LOADING.
		state.checkOutState.bikeFleet = CheckOutStateEnum.LOADING
		// Reset any existing error to false.
		state.error.bikeFleet = false
	})

	builder.addCase(getBikeFleetThunk.rejected, (state, action) => {
		// If the fetch fails, set the bike fleet checkout state to ERROR.
		state.checkOutState.bikeFleet = CheckOutStateEnum.ERROR
		// Store the error message.
		state.error.bikeFleet = action.payload as string
	})

	builder.addCase(getBikeFleetThunk.fulfilled, (state, action) => {
		// When the fetch is successful:
		// Extract the status from the action's metadata.
		const status = action.meta.arg.status
		// Map through the payload to add a 'choice' property to each bike.
		const bikeFleetList = action.payload.map((bike) => ({ ...bike, choice: false }))
		// Filter bikes based on the status, if status is not 'ALL'.
		const filteredBikeFleet = bikeFleetList.filter((bike) =>
			Number(status) !== StatusEnum.ALL ? bike.bike_status === Number(status) : bike
		)
		// Set the checkout state to READY, indicating data is ready to use.
		state.checkOutState.bikeFleet = CheckOutStateEnum.READY
		// Update the bikeFleet state with the new list.
		state.bikeFleet = bikeFleetList
		// Apply transformation to the filtered bike fleet and update the state.
		state.filteredBikeFleet = transformBikeFleet(filteredBikeFleet)
		// Reset any existing error to false.
		state.error.bikeFleet = false
	})
}

export const handleUpdateBikeStatusBuilder = (
	builder: ActionReducerMapBuilder<BikeFleetStateProps>
) => {
	// Handles state changes when updating the status of a bike.
	builder.addCase(updateBikeStatusThunk.pending, (state) => {
		// When the update starts, set the update bike checkout state to LOADING.
		state.checkOutState.updateBike = CheckOutStateEnum.LOADING
		// Reset any existing error to false and clear previous update results.
		state.error.updateBike = false
		state.updateBike.error = []
		state.updateBike.success = []
	})

	builder.addCase(updateBikeStatusThunk.rejected, (state, action) => {
		// If the update fails, set the update bike checkout state to ERROR.
		state.checkOutState.updateBike = CheckOutStateEnum.ERROR
		// Store the error messages.
		state.updateBike.error = action.payload as string[]
	})

	builder.addCase(updateBikeStatusThunk.fulfilled, (state, action) => {
		// When the update is successful:
		// Set the update bike checkout state to READY.
		state.checkOutState.updateBike = CheckOutStateEnum.READY
		// Update the success and error states with the response.
		state.updateBike.success = action.payload.success
		state.updateBike.error = action.payload.error
		// Prepare a list of bike IDs that were successfully updated.
		const bikeIdSuccessList = action?.payload.success.map((i) => i.bike_id)
		const bikeIdErrorList = action.payload.error
		// Update the status of bikes in the bike fleet, based on the update results.
		// Status, Choice, row_version
		// Remove bike based on destroy mode
		const modifiedBikesWithNewStatus =
			action.meta.arg.bikeStatus === StatusEnum.DESTRUCT
				? state.bikeFleet.filter((bike) => !bikeIdSuccessList.includes(bike.bike_id))
				: state.bikeFleet.map((bike) => {
						const isBikeSuccess = bikeIdSuccessList.includes(bike.bike_id)
						const rowVersion = isBikeSuccess
							? action.payload.success.find(
									(bikeSuccess) => bikeSuccess.bike_id === bike.bike_id
							  )?.row_version
							: bike.row_version
						const choice = isBikeSuccess ? false : bike.choice
						const status = isBikeSuccess ? action.meta.arg.bikeStatus : bike.bike_status
						return {
							...bike,
							bike_status: status,
							choice: choice,
							row_version: rowVersion,
						}
				  })
		// Update the bikeFleet state with these changes.
		state.bikeFleet = modifiedBikesWithNewStatus

		// Reset global checkbox
		state.selectBikeFleetAll = false
		// Reset select checkbox when list bike error is empty
		if (!isNotEmpty(bikeIdErrorList)) state.currentBikeFleetStatusSelected = false
	})
}
